import './Modal.css';
import '../Animate/animate.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useEffect, useState } from 'react';
import { getAllUsers } from '../../api/users_institutions';
import { getExamAdminPermissions, getTestAdminPermissions } from '../../api/admin_permissions';

const isTempId = (id) => typeof id === 'string' && id.startsWith('temp-');

function ModalExam({
    Action, selectedTopic, selectedLevel, selectedExam, selectedTest, examType, selectedExamAttempt, closeModal,
    tests, stageCreateExam, stageUpdateExam, stageDeleteExam,
    stageCreateTopicContent, stageUpdateTopicContent, stageDeleteTopicContent,
    stageCreateTest, stageUpdateTest, confirmDiscardUnsavedChanges,
    examAdminAccess, stageSetExamAdminAccess, testAdminAccess, stageSetTestAdminAccess,
}) {

    const { t } = useTranslation();
    const [animationClass, setAnimationClass] = useState('anim-initial');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedAdminUserIds, setSelectedAdminUserIds] = useState([]);

    // The Test's own id (TestAdminPermission.test FK) -- selectedTest is the topic-content row,
    // not the Test itself, so this resolves through `tests` like handleUpdateExam already does.
    // Both are undefined in Add mode (no exam/test exists yet).
    const selectedTestRowId = tests.find(tst => tst.topic_content === selectedTest?.id)?.id;
    const selectedPermissionItemId = examType === 'exam' ? selectedExam?.id : selectedTestRowId;
    const stagedAccessMap = examType === 'exam' ? examAdminAccess : testAdminAccess;

    useEffect(() => {
        let cancelled = false;

        const loadAdminUsers = async () => {
            try {
                const users = await getAllUsers();
                const staffUsers = Array.isArray(users) ? users.filter((user) => user.is_staff && !user.is_superuser) : [];
                if (!cancelled) {
                    setAdminUsers(staffUsers);
                }
            } catch (error) {
                console.error('Error loading admin users:', error);
                if (!cancelled) {
                    setAdminUsers([]);
                }
            }
        };

        loadAdminUsers();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        // A selection already staged this session (Level.jsx's exam/testAdminAccess map)
        // reflects what the user actually picked -- authoritative over whatever's still in the
        // database, and the only source of truth for an exam/test created this session (temp id,
        // no DB row yet).
        const stagedSelection = selectedPermissionItemId !== undefined ? stagedAccessMap?.[selectedPermissionItemId] : undefined;
        if (stagedSelection !== undefined) {
            setSelectedAdminUserIds(stagedSelection);
            return;
        }

        setSelectedAdminUserIds([]);

        const loadSelectedItemAdminAccess = async () => {
            if (!selectedPermissionItemId || isTempId(selectedPermissionItemId)) {
                return;
            }

            try {
                const permissions = examType === 'exam' ? await getExamAdminPermissions() : await getTestAdminPermissions();
                const userIds = Array.isArray(permissions)
                    ? permissions
                        .filter((permission) => {
                            const itemId = examType === 'exam' ? (permission.exam?.id ?? permission.exam) : (permission.test?.id ?? permission.test);
                            return Number(itemId) === Number(selectedPermissionItemId);
                        })
                        .map((permission) => permission.user?.id ?? permission.user)
                        .filter((id) => id !== undefined && id !== null)
                    : [];

                if (!cancelled) {
                    setSelectedAdminUserIds(userIds);
                }
            } catch (error) {
                console.error('Error loading admin access:', error);
                if (!cancelled) {
                    setSelectedAdminUserIds([]);
                }
            }
        };

        loadSelectedItemAdminAccess();

        return () => {
            cancelled = true;
        };
    }, [examType, selectedPermissionItemId, stagedAccessMap]);

    const toggleAdminAccessBtn = (userId) => {
        setSelectedAdminUserIds((currentIds) =>
            currentIds.includes(userId)
                ? currentIds.filter((currentId) => currentId !== userId)
                : [...currentIds, userId]
        );
    };

    const renderAdminAccessCheckboxes = () => {
        return adminUsers.map((admin) => (
            <button
                key={admin.id}
                type="button"
                className={`access-btn${selectedAdminUserIds.includes(admin.id) ? ' active' : ''}`}
                data-val={admin.username}
                onClick={() => toggleAdminAccessBtn(admin.id)}
            >
                {admin.first_name || admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : admin.username}
            </button>
        ));
    };

    const renderAdminAccessContainer = () => (
        <div id="editModuleAdminAccessContainer" style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
            <label className="label-text">{t('modalExam.adminAccessLabel')} <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{t('modalExam.adminAccessHint')}</span></label>
            <div className="access-btn-group" id="editModuleAdminAccessGroup" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {renderAdminAccessCheckboxes()}
            </div>
        </div>
    );

    const handleCreateExam = async () => {
        const title = document.getElementById('newExamTitle').value;
        const duration = document.getElementById('newExamDuration').value;
        const score = document.getElementById('newExamScore').value || 100; // Default to 100

        if (!selectedLevel && !selectedTopic) return alert(t('modalExam.alerts.selectLevelTopicFirst'));
        if (!examType) return alert(t('modalExam.alerts.examTypeNotSpecified'));
        if(!title) return alert(t('modalExam.alerts.titleRequired'));
        if(!duration) return alert(t('modalExam.alerts.durationRequired'));
        if(!score) return alert(t('modalExam.alerts.maxScoreRequired'));

        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (examType === 'exam') {
                const examData = {
                    level: selectedLevel?.id || null,
                    title: title,
                    duration: parseInt(duration),
                    questions_count: 0,
                    max_score: parseInt(score),
                    is_visible: document.getElementById('newExamVisibility').checked,
                };
                const newExam = await stageCreateExam(examData);
                stageSetExamAdminAccess(newExam.id, selectedAdminUserIds);
                console.log('Exam staged:', newExam);

            } else if (examType === 'test') {

                const newTopicContent = await stageCreateTopicContent({
                    topic: selectedTopic?.id || null,
                    type: 'test',
                    is_visible: document.getElementById('newExamVisibility').checked,
                    order: 0,
                })

                const examData = {
                    topic_content: newTopicContent.id || null,
                    title: title,
                    duration: parseInt(duration),
                    questions_count: 0,
                    max_score: parseInt(score),
                };
                const newTest = await stageCreateTest(examData);
                stageSetTestAdminAccess(newTest.id, selectedAdminUserIds);
                console.log('Test staged:', newTest);
            }
            closeModal(true);
        } catch (error) {
            console.error('Error staging exam/test creation:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateExam = async () => {
        const title = document.getElementById('newExamTitle').value;
        const duration = document.getElementById('newExamDuration').value;
        const score = document.getElementById('newExamScore').value || 100; // Default to 100

        if (!selectedLevel && !selectedTopic) return alert(t('modalExam.alerts.selectLevelTopicFirst'));
        if (!examType) return alert(t('modalExam.alerts.examTypeNotSpecified'));
        if(!title) return alert(t('modalExam.alerts.titleRequired'));
        if(!duration) return alert(t('modalExam.alerts.durationRequired'));
        if(!score) return alert(t('modalExam.alerts.maxScoreRequired'));

        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (examType === 'exam') {
                const examData = {
                    level: selectedLevel?.id || null,
                    title: title,
                    duration: parseInt(duration),
                    questions_count: selectedExam?.questions_count ?? 0,
                    max_score: parseInt(score),
                    is_visible: document.getElementById('editExamVisibility').checked,
                };
                const updatedExam = await stageUpdateExam(selectedExam.id, examData);
                stageSetExamAdminAccess(selectedExam.id, selectedAdminUserIds);
                console.log('Exam staged as updated:', updatedExam);
            } else if (examType === 'test') {
                const test = tests.find(tst => tst.topic_content === selectedTest.id);
                const examData = {
                    topic_content: selectedTest?.id || null,
                    title: title,
                    duration: parseInt(duration),
                    questions_count: test?.questions_count ?? 0,
                    max_score: parseInt(score),
                };
                const updatedTest = await stageUpdateTest(test?.id, examData);
                stageSetTestAdminAccess(test?.id, selectedAdminUserIds);

                // selectedTest here IS the topic-content row (not the Topic) -- its own
                // type/order, not the topic's, are what this update should carry forward.
                const topicContentData = {
                    topic: selectedTopic?.id || null,
                    type: selectedTest?.type || 'test',
                    is_visible: document.getElementById('editExamVisibility').checked,
                    order: selectedTest?.order || 0,
                }
                const updatedTopicContent = await stageUpdateTopicContent(selectedTest.id, topicContentData);

                console.log('Test staged as updated:', updatedTest, updatedTopicContent);
            }
            closeModal(true);
        } catch (error) {
            console.error('Error staging exam/test update:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteExam = async () => {
        if (!examType) return alert(t('modalExam.alerts.examTypeNotSpecified'));
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (examType === 'exam') {
                await stageDeleteExam(selectedExam.id);
                console.log('Exam staged as deleted:', selectedExam.id);
            } else if (examType === 'test') {
                await stageDeleteTopicContent(selectedTest?.id);
                console.log('Test staged as deleted');
            }
            closeModal(true);
        } catch (error) {
            console.error('Error staging exam/test deletion:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const navigate = useNavigate();
    const shouldResetExamChoices = selectedExamAttempt?.completed === true;
    const shouldResetTestChoices = selectedExamAttempt?.completed === true;
    const shouldResumeExam = selectedExamAttempt?.completed === false;
    const shouldResumeTest = selectedExamAttempt?.completed === false;

    const handleStartExam = () => {
        if (confirmDiscardUnsavedChanges && !confirmDiscardUnsavedChanges()) return;
        setAnimationClass('anim-exit-left');
        if (selectedExam) {
            setTimeout(() => {
                console.log('Starting exam:', selectedExam);
                navigate(`/dashboard/${selectedLevel.title}/exams/${selectedExam.title}`, { state: { selectedExam: selectedExam, resetUserExamChoices: shouldResetExamChoices } });
            }, 300); // Match this duration with your CSS animation duration
        } else {
            alert(t('modalExam.alerts.noExamSelected'));
        }
        closeModal();
    }

    const handleStartTest = () => {
        if (confirmDiscardUnsavedChanges && !confirmDiscardUnsavedChanges()) return;
        setAnimationClass('anim-exit-left');
        if (selectedTest) {
            const test = tests.find(tst => tst.topic_content === selectedTest.id);
            console.log('Starting test:', selectedTest);
            navigate(`/dashboard/${selectedLevel.title}/${selectedTopic.title}/tests/${test?.title}`, { state: { selectedTest: test, resetUserTestChoices: shouldResetTestChoices } });
        } else {
            alert(t('modalExam.alerts.noTestSelected'));
        }
        closeModal();
    }
    return (
        <>
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
            <div className="Modal">
            {Action === 'Add' ? (
                <div className="modal-box">
                    <h2 style={{marginTop: 0, color: 'var(--exam-color)'}}>{examType === 'exam' ? t('modalExam.addNewExam') : t('modalExam.addNewTest')}</h2>
                    <label className="label-text">{t('modalExam.titleLabel')}</label>
                    <input type="text" id="newExamTitle" className="login-input" defaultValue="Exam" />

                    <label className="label-text">{t('modalExam.durationMinutesLabel')}</label>
                    <input type="number" id="newExamDuration" className="login-input" placeholder={t('modalExam.durationPlaceholder')} />

                    <label className="label-text">{t('modalExam.maxScoreLabel')}</label>
                    <input type="number" id="newExamScore" className="login-input" placeholder={t('modalExam.maxScorePlaceholder')} />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="newExamVisibility" defaultChecked={selectedExam?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}

                    <div className="modal-buttons">
                        <button className="btn-primary" style={{background: 'var(--exam-color)'}} onClick={handleCreateExam} disabled={isSubmitting}>{examType === 'exam' ? t('modalExam.createExam') : t('modalExam.createTest')}</button>
                        <button className="btn-primary btn-secondary" onClick={closeModal}>{t('common.cancel')}</button>
                    </div>
                </div>
            ) : Action === 'Update' ? (
                <div className="modal-box">
                    <h2 style={{marginTop: 0, color: 'var(--exam-color)'}}>{examType === 'exam' ? t('modalExam.editExam') : t('modalExam.editTestTitle')}</h2>
                    <label className="label-text">{t('modalExam.titleLabel')}</label>
                    <input type="text" id="newExamTitle" className="login-input" defaultValue={examType === 'exam' ? selectedExam?.title || '' : tests.find(tst => tst.topic_content === selectedTest.id)?.title || ''} />

                    <label className="label-text">{t('modalExam.durationMinutesLabel')}</label>
                    <input type="number" id="newExamDuration" className="login-input" defaultValue={examType === 'exam' ? selectedExam?.duration || '' : tests.find(tst => tst.topic_content === selectedTest.id)?.duration || ''} />

                    <label className="label-text">{t('modalExam.maxScoreLabel')}</label>
                    <input type="number" id="newExamScore" className="login-input" defaultValue={examType === 'exam' ? selectedExam?.max_score || '' : tests.find(tst => tst.topic_content === selectedTest.id)?.max_score || ''} />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="editExamVisibility" defaultChecked={examType === 'test' ? selectedTest?.is_visible : selectedExam?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}

                    <div className="modal-buttons">
                        <button className="btn-primary" style={{background: 'var(--exam-color)'}} onClick={() => handleUpdateExam()} disabled={isSubmitting}>{examType === 'exam' ? t('modalExam.updateExam') : t('modalExam.updateTest')}</button>
                        <button className="btn-primary btn-secondary" onClick={closeModal}>{t('common.cancel')}</button>
                    </div>
                </div>
            ) : Action === 'Delete' ? (
                <div className="modal-box">
                    <h2>{examType === 'exam' ? t('modalExam.deleteExamTitle') : t('modalExam.deleteTestTitle')} </h2>
                    <p>{examType === 'exam' ? t('modalExam.deleteExamConfirm') : t('modalExam.deleteTestConfirm')}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        <strong>{examType === 'exam' ? selectedExam?.title : tests.find(tst => tst.topic_content === selectedTest.id)?.title}</strong>
                    </p>
                    <div className="modal-buttons">
                        <button
                            className="btn-primary btn-delete"
                            style={{ background: 'var(--del-color)' }}
                            onClick={() => handleDeleteExam()}
                            disabled={isSubmitting}
                        >
                            {t('common.delete')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Start' ? (
            <div className="modal-box modal-box-centered">
                <h2 style={{marginTop: 0}} id="startTestTitle">{examType === 'exam' ? t('modalExam.startExamQuestion') : t('modalExam.startTestQuestion')}</h2>
                <p style={{color: 'var(--text-muted)', fontSize: '1.1rem', marginBottom: '25px'}}>
                    {t('modalExam.gradedAssessmentWarning')}
                </p>

                <div className="test-stats-simple">
                    <div className="test-stat-cell">
                        <div className="ts-value" id="startTestDuration">{examType === 'exam' ? selectedExam?.duration : tests.find(tst => tst.topic_content === selectedTest.id)?.duration}</div>
                        <div className="ts-label">{t('modalExam.minutesLabel')}</div>
                    </div>
                    <div className="test-stat-cell">
                        <div className="ts-value" id="startTestQuestions">{examType === 'exam' ? selectedExam?.questions_count : tests.find(tst => tst.topic_content === selectedTest.id)?.questions_count}</div>
                        <div className="ts-label">{t('modalExam.questionsLabel')}</div>
                    </div>
                    <div className="test-stat-cell">
                        <div className="ts-value" id="startTestScore">{examType === 'exam' ? selectedExam?.max_score : tests.find(tst => tst.topic_content === selectedTest.id)?.max_score}</div>
                        <div className="ts-label">{t('modalExam.scoreLabel')}</div>
                    </div>
                </div>

                <div style={{display: 'flex', gap: '10px'}}>
                    <button className="btn-primary" onClick={examType === 'exam' ? handleStartExam : handleStartTest}>{examType === 'exam' ? (shouldResumeExam ? t('modalExam.resumeExam') : t('modalExam.startExam')) : (shouldResumeTest ? t('modalExam.resumeTest') : t('modalExam.startTest'))}</button>
                    <button className="btn-primary" style={{background: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border)'}} onClick={closeModal}>{t('common.cancel')}</button>
                </div>
            </div>

            ) : null}
        </div>
        </>
    );
}

export default ModalExam;
